import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { useRouter, useServices } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import { evalCondition } from 'core/common/utils/evalCondition';
import {
  BasePageAttributes,
  BasePageAttributesWithMappedSections,
  BaseScreenAttributes,
  DynamicUserProfile,
  DynamicUserProfileValue,
  Flow,
  ScreenType,
  SectionAttributes,
  StepType,
} from 'core/flow/entities';
import { getConditionAttributes } from 'core/funnel/store';
import { useUser } from 'core/user/hooks';

type Params = {
  screens: Flow['screens'];
  flowInitialScreenId: string;
  dynamicUserProfile: DynamicUserProfile | undefined;
  getAttributesByFeature: <
    TAttributes extends BaseScreenAttributes | BasePageAttributes | SectionAttributes,
  >(
    attributes: Array<TAttributes>,
  ) => TAttributes;
  getAttributesByCondition: <
    TAttributes extends BaseScreenAttributes | BasePageAttributes | SectionAttributes,
  >(
    attributes: Array<TAttributes>,
  ) => TAttributes;
  transformContentToTemplate: (content: Record<string, unknown>) => {
    [x: string]: unknown;
  };
};

export const useFlowAttributes = ({
  screens,
  dynamicUserProfile,
  flowInitialScreenId,
  getAttributesByFeature,
  getAttributesByCondition,
  transformContentToTemplate,
}: Params) => {
  const { asPath } = useRouter();
  const { updateUserPreferences } = useUser();
  const { loggingService: logger } = useServices();

  const store = useStore<RootInitialState>();

  const getPageAttributes = (
    attributes: Array<BasePageAttributes>,
  ): BasePageAttributesWithMappedSections => {
    const funnelConditions = getConditionAttributes(store.getState());

    const screensWithCondition = attributes.filter((item) => {
      if (item.condition) {
        return !![funnelConditions].find(evalCondition(item.condition));
      }

      return false;
    });

    if (screensWithCondition.length) {
      const featureBasedAttributesWithCondition = getAttributesByFeature(screensWithCondition);

      return {
        ...featureBasedAttributesWithCondition,
        sections: getPageSections(featureBasedAttributesWithCondition.sections),
      };
    }

    const screensWithoutCondition = attributes.filter((item) => !item.condition);

    const featureBasedAttributes = getAttributesByFeature(screensWithoutCondition);

    return {
      ...featureBasedAttributes,
      sections: getPageSections(featureBasedAttributes.sections),
    };
  };

  const getQuizStepAttributes = (attributes: Array<BaseScreenAttributes>): BaseScreenAttributes => {
    const funnelConditions = getConditionAttributes(store.getState());

    const screensWithCondition = attributes.filter((item) => {
      if (item.condition) {
        return !![funnelConditions].find(evalCondition(item.condition));
      }

      return false;
    });

    if (screensWithCondition.length) {
      const featureBasedAttributesWithCondition = getAttributesByFeature(screensWithCondition);

      return {
        ...featureBasedAttributesWithCondition,
        content: transformContentToTemplate(featureBasedAttributesWithCondition.content),
      };
    }

    const screensWithoutCondition = attributes.filter((item) => !item.condition);

    const featureBasedAttributes = getAttributesByFeature(screensWithoutCondition);

    return {
      ...featureBasedAttributes,
      content: transformContentToTemplate(featureBasedAttributes.content),
    };
  };

  const getDefaultAttributes = (
    attributes: Array<BaseScreenAttributes | BasePageAttributes>,
  ): BaseScreenAttributes | BasePageAttributesWithMappedSections => {
    const attributesLastIndex = attributes.length - 1;

    if (attributes.every((attr) => 'sections' in attr)) {
      const lastAttribute = attributes[attributesLastIndex] as BasePageAttributes;

      return {
        ...lastAttribute,
        sections: getDefaultPageSections(lastAttribute.sections),
      };
    }

    return attributes[attributesLastIndex] as BaseScreenAttributes;
  };

  const checkIfAttributesContainFeature = (
    attributes: Array<BasePageAttributes | BaseScreenAttributes>,
  ) => {
    return attributes.some((item) => {
      if (item.feature || item.experiment) {
        return true;
      }

      if ('sections' in item) {
        return item.sections.some((section) =>
          section.attributes.some((attr) => attr.feature || attr.experiment),
        );
      }

      return false;
    });
  };

  const getDefaultPageSections = (sections: BasePageAttributes['sections']) => {
    const sectionsByCondition = sections.map((section) => {
      const attrsByCondition = getAttributesByCondition(section.attributes);

      if (!attrsByCondition) {
        const attrsWithoutConditions = section.attributes.filter((attrs) => !attrs.condition);

        return attrsWithoutConditions[attrsWithoutConditions.length - 1];
      }

      return attrsByCondition;
    });

    const sectionsWithMappedContent = sectionsByCondition.map((section) => {
      return {
        ...section,
        content: transformContentToTemplate(section.content),
      };
    });

    return sectionsWithMappedContent as BasePageAttributesWithMappedSections['sections'];
  };

  const getPageSections = (sections: BasePageAttributes['sections']) => {
    const funnelConditions = getConditionAttributes(store.getState());

    const getFilteredAttributes = (attributes: Array<SectionAttributes>) => {
      return attributes.filter(
        (attrs) => attrs.condition && [funnelConditions].find(evalCondition(attrs.condition)),
      );
    };

    const sectionsByCondition = sections.map((section) => {
      const attrsByCondition = getFilteredAttributes(section.attributes);

      if (!attrsByCondition.length) {
        const attrsWithoutConditions = section.attributes.filter((attrs) => !attrs.condition);

        return getAttributesByFeature(attrsWithoutConditions);
      }

      return getAttributesByFeature(attrsByCondition);
    });

    const sectionsWithMappedContent = sectionsByCondition.map((section) => {
      return {
        ...section,
        content: transformContentToTemplate(section.content),
      };
    });

    return sectionsWithMappedContent as BasePageAttributesWithMappedSections['sections'];
  };

  const getDynamicAttributesByCondition = (rules: DynamicUserProfileValue['rules']) => {
    const conditions = getConditionAttributes(store.getState());

    const matchedAttributes = rules.find(
      (rule) => rule.condition && [conditions].find(evalCondition(rule.condition)),
    );

    return matchedAttributes;
  };

  const updateDynamicAttributes = useCallback(
    (dataKey: string, pageId: string) => {
      if (!dynamicUserProfile) return;

      for (const [key, value] of Object.entries(dynamicUserProfile)) {
        const dataKeyTriggers = value.rules.flatMap(({ condition }) => {
          return Object.keys(condition);
        });

        const triggeredByDataKey = dataKeyTriggers.includes(dataKey);
        const triggeredByScreenId = value.customScreenIdTriggers?.includes(pageId);

        if (triggeredByDataKey || triggeredByScreenId) {
          const dynamicAttributes = getDynamicAttributesByCondition(value.rules);

          if (dynamicAttributes) {
            updateUserPreferences({ [key]: dynamicAttributes.value });
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicUserProfile, updateUserPreferences],
  );

  const getScreenAttributes = (
    pageId: string,
  ): BaseScreenAttributes | BasePageAttributesWithMappedSections => {
    const screen = screens.find((item) => item.id === pageId);
    const isNextScreen = asPath.split('/').pop() !== pageId;

    if (!screen) {
      logger.warn('Screen attributes not found');

      const initialScreen =
        screens.find((screenItem) => screenItem.id === flowInitialScreenId) ?? screens[0];
      const isPage = initialScreen.type === StepType.PAGE;

      return isPage
        ? getPageAttributes(initialScreen.attributes as Array<BasePageAttributes>)
        : getQuizStepAttributes(initialScreen.attributes as Array<BaseScreenAttributes>);
    }

    const screenType = screen.type ?? StepType.QUIZ_STEP;
    const screenAttributes = screen.attributes as Array<BasePageAttributes | BaseScreenAttributes>;
    const screenWithFeature = checkIfAttributesContainFeature(
      screenAttributes as Array<BasePageAttributes | BaseScreenAttributes>,
    );

    const doesAttributesContainSplit = screenAttributes.some(
      (attrs) => attrs.screenType === ScreenType.SPLIT,
    );
    const shouldReturnDefaultAttributes =
      isNextScreen && screenWithFeature && !doesAttributesContainSplit;

    if (shouldReturnDefaultAttributes) {
      return getDefaultAttributes(
        screenAttributes as Array<BaseScreenAttributes | BasePageAttributes>,
      );
    }

    return screenType === StepType.PAGE
      ? getPageAttributes(screen.attributes as Array<BasePageAttributes>)
      : getQuizStepAttributes(screen.attributes as Array<BaseScreenAttributes>);
  };

  return { getScreenAttributes, updateDynamicAttributes };
};
